/**
 * Client pentru admin API (aceeași origine). Tokenul de sesiune (multi-session
 * auth - vezi POST /login) stă în localStorage; un 401 îl curăță și trimite
 * userul înapoi la autentificare.
 */

const KEY_STORAGE = "aig_session";

export function getKey(): string | null {
  return localStorage.getItem(KEY_STORAGE);
}
export function setKey(key: string): void {
  localStorage.setItem(KEY_STORAGE, key);
}
export function clearKey(): void {
  localStorage.removeItem(KEY_STORAGE);
}

// Tokenul de admin păstrat cât timp o sesiune de impersonare e activă (KEY_STORAGE
// devine, în acel interval, tokenul CLIENTULUI) - vezi enterImpersonation/exitImpersonation
// din main.tsx.
const IMPERSONATION_RETURN_KEY = "aig_impersonation_return";

export function getImpersonationReturn(): string | null {
  return localStorage.getItem(IMPERSONATION_RETURN_KEY);
}
export function setImpersonationReturn(token: string): void {
  localStorage.setItem(IMPERSONATION_RETURN_KEY, token);
}
export function clearImpersonationReturn(): void {
  localStorage.removeItem(IMPERSONATION_RETURN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public data: unknown,
  ) {
    super(`API ${status}`);
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const key = getKey();
  // FormData (upload de logo): NU setăm content-type - fetch pune boundary-ul automat
  const isFormData = init?.body instanceof FormData;
  const res = await fetch(`/v1/admin${path}`, {
    ...init,
    headers: {
      ...(isFormData ? {} : { "content-type": "application/json" }),
      // x-admin-key, nu Authorization: acela e ocupat de basic auth-ul din Caddy
      ...(key ? { "x-admin-key": key } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const data = await res.json().catch(() => null);
  if (res.status === 401 && key && !isWrongPassword(data)) {
    clearKey();
    location.reload();
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

/**
 * 401 cu „parolă greșită" (schimbarea parolei, ștergerea contului) NU înseamnă
 * sesiune moartă: sesiunea e bună, doar parola tastată e greșită. Fără excepția
 * asta, request() ar deloga omul și ar reîncărca pagina în loc să-i arate eroarea.
 */
export function isWrongPassword(data: unknown): boolean {
  const code = (data as { error?: string } | null)?.error;
  return code === "invalid credentials" || code === "invalid_credentials";
}

// Oglindă manuală a `PlanId` din packages/backend/src/services/plans.ts -
// dashboard-ul nu importă din backend, deci compilatorul NU prinde o
// desincronizare: un plan nou lipsă de aici apare la runtime ca `undefined` în
// tabelele indexate pe PlanId (ex. PLAN_ICON). Ține-le împreună.
// "free" nu se cumpără: nu apare pe cardurile de preț (backendul trimite
// ALL_PLANS către client), doar în consola de admin, care primește ASSIGNABLE_PLANS.
export type PlanId = "free" | "starter" | "growth" | "agency" | "enterprise";

/** țara + codul poștal declarate înainte de checkout (IGIC pentru Canare) */
export interface BillingLocation {
  country: string;
  postalCode?: string;
}

/** Datele de firmă (B2B) - obligatorii la register + poarta de profil. Exact
 *  setul pe care backendul îl cere și-l împinge în Stripe (vezi G19). */
export interface BusinessProfileInput {
  companyName: string;
  taxId: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postalCode: string;
  country: string;
}

/** Forma stocată (nullable) - vine din /me și /account/profile. */
export interface BusinessProfile {
  companyName: string | null;
  taxId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
}

/** Vezi packages/backend/src/services/plans.ts pentru semantica exactă. */
export type BrandingLevel = "amwai" | "custom" | "white-label";

// doar capabilitățile efectiv gate-uite - vezi comentariul din plans.ts (3h)
export type PlanFeature = "analytics" | "clientManagement" | "api";

export interface Plan {
  id: PlanId;
  label: string;
  priceEur: number;
  sites: number;
  answersPerMonth: number;
  pageLimit: number;
  brandingLevel: BrandingLevel;
  features: PlanFeature[];
  /** plan „contact us" (Enterprise): fără preț afișat, fără self-serve */
  contactOnly?: boolean;
  tagline: string;
  highlights: string[];
}

export interface PlanSummary {
  plan: PlanId;
  planLabel: string;
  priceEur: number;
  brandingLevel: BrandingLevel;
  features: PlanFeature[];
  sitesUsed: number;
  sitesLimit: number;
  answersUsed: number;
  answersLimit: number;
  answersRemaining: number;
  overageCreditMicroUsd: number;
  /** scadența abonamentului (ms); null = cont neactivat încă */
  renewsAt: number | null;
  /** data exactă la care se golește cota lunară - vezi comentariul din backend */
  answersResetAt: number;
  /** day of month (1-31) the monthly stats/quota resets; null = automatic (derived from payment or registration date) */
  statsResetDay: number | null;
  /** true = abonament Stripe activ → cardurile arată Upgrade/Downgrade, nu Subscribe */
  hasStripeSubscription: boolean;
  /** downgrade programat la finalul perioadei; null = niciunul */
  pendingPlan: PlanId | null;
  /** abonamentul se încheie la `renewsAt` (anulat din portalul Stripe sau prin ștergerea contului) */
  cancelAtPeriodEnd: boolean;
  /** sloturi „extra site" active (deja incluse în sitesLimit) */
  extraSites: number;
  /** pool-ul one-time de bonus („more messages"), informativ */
  bonusAnswers: number;
  /**
   * Grant intern de plan acordat de echipă; null = fără grant. Limitele de mai
   * sus îl reflectă DEJA - câmpul e aici doar ca UI-ul să poată explica de ce
   * clientul are mai mult decât planul lui. `plan`/`priceEur` rămân cele plătite.
   */
  grantedPlan: PlanId | null;
  /**
   * Cota lunară e epuizată → widgetul nu se mai arată vizitatorilor NOI pe
   * site-urile contului. Calculat de backend (services/adminService.ts), care
   * folosește aceeași definiție ca `isAnswersExhausted` din usage.ts.
   */
  answersExhausted: boolean;
}

/**
 * Un eveniment de audit - cine a făcut ce, când (vezi backend services/audit.ts
 * și docs/plan-audit.md). DOAR pentru consola de super-admin: nu există endpoint
 * care să expună auditul clientului.
 *
 * `actorType` poate fi și "unknown" pe rândurile de dinainte de migrația 0022,
 * care n-aveau noțiunea de actor.
 */
export interface AuditEntry {
  id: number;
  createdAt: number;
  action: string;
  actorType: "account" | "admin" | "system" | "anonymous" | "unknown";
  actorAccountId: number | null;
  accountId: number | null;
  /** din join cu accounts: null pe conturi purjate sau evenimente fără cont */
  accountEmail: string | null;
  appId: string | null;
  ip: string | null;
  meta: Record<string, unknown> | null;
  /** @deprecated text liber, doar pe rândurile vechi */
  detail: string | null;
  requestId: string | null;
}

export interface Me {
  email: string;
  isAdmin: boolean;
  emailVerified: boolean;
  creditMicroUsd: number;
  /** are parolă proprie (register email); false = cont doar-Google → fără „schimbă parola". */
  hasPassword: boolean;
  /** consimțământ pentru emailuri de marketing (newsletter, oferte) */
  marketingConsent: boolean;
  /** poarta „profil complet": false → dashboardul afișează formularul de completare
   *  a datelor de firmă (cazul tipic: cont Google proaspăt). */
  profileComplete: boolean;
  business: BusinessProfile;
  plan: PlanSummary;
  /**
   * Redemption-ul contului încă neconsumat (orice cod - referral, promo sau
   * founding offer): null = niciunul. `months` (1 = doar prima plată, >1 = și
   * facturile următoare) există ca să distingem oferta de lansare (3 luni) de
   * un cod de referral obișnuit (1 lună) - vezi resolveFoundingDiscount, care
   * respectă codul clientului în loc să-l suprascrie cu FOUNDING30.
   */
  firstPaymentDiscount: { code: string; percent: number; months: number } | null;
  plans: Plan[];
  overage: { eurPerBlock: number; blockAnswers: number; maxBlocks: number };
  addons: { extraSiteEur: number };
  /** număr de tickete de suport deschise (0 pentru non-admini) - badge în navigație */
  openTickets: number;
  /** true doar pe o sesiune de impersonare admin - vezi POST /console/accounts/:id/impersonate. */
  impersonating: boolean;
}

/**
 * Oferta „founding" (−X% primele Y luni, primii 100 de clienți plătitori,
 * cod FOUNDING30) - vezi GET /v1/public/launch-offer în backend. Contractul
 * NU expune niciodată numărul de sloturi rămase, doar starea.
 */
export type LaunchOffer =
  | { status: "none" }
  | { status: "active"; percent: number; months: number; code: string; cap?: number }
  | { status: "almost_gone"; percent: number; months: number; code: string; cap?: number }
  | { status: "claimed" };

/**
 * „Contul are un abonament activ" - abonament Stripe, sau o scadență setată
 * manual de noi (`markPaid`). Backendul nu are un echivalent: acolo adevărul e
 * planul contului, iar un cont fără abonament stă pe planul `free`.
 *
 * Aici e nevoie de el fiindcă UI-ul trebuie să distingă „n-a cumpărat încă" de
 * „e pe planul X", inclusiv în cazul rar în care un admin a mutat manual un cont
 * pe un plan plătit fără să-i activeze scadența.
 */
export function hasActiveSubscription(plan: Pick<PlanSummary, "hasStripeSubscription" | "renewsAt">): boolean {
  return plan.hasStripeSubscription || plan.renewsAt !== null;
}

/**
 * Epuizarea NU se calculează aici: vine gata calculată din backend
 * (`PlanSummary.answersExhausted`, services/adminService.ts), fiindcă e aceeași
 * regulă care decide dacă widgetul mai apare pe site-urile clientului. O a doua
 * implementare în dashboard ar putea diverge, iar simptomul ar fi cel mai prost
 * cu putință: dashboardul spune „totul e bine" cu widgetul stins.
 */
export function isQuotaExhausted(plan: PlanSummary): boolean {
  return plan.answersExhausted;
}

/**
 * Prag de avertizare - 80% din limita lunară de răspunsuri, ca să oglindească
 * emailul de la același prag (`quotaNotifications.ts`, WARNING_THRESHOLD_RATIO).
 * Deliberat independent de bonus/credit (la fel ca emailul): e un semnal
 * timpuriu, nu o afirmație „widgetul se stinge acum" - aia e doar `isQuotaExhausted`.
 */
export function isQuotaWarning(plan: PlanSummary): boolean {
  return plan.answersLimit > 0 && plan.answersUsed / plan.answersLimit >= 0.8;
}

/** true doar pentru conturile care încă n-au plătit nimic - discountul e doar
 * pe primul checkout, altfel i-am minți prețul unui abonat deja activ.
 * Exprimat prin `hasActiveSubscription` ca cele două să nu poată diverge. */
export function foundingOfferEligible(plan: Pick<PlanSummary, "hasStripeSubscription" | "renewsAt">): boolean {
  return !hasActiveSubscription(plan);
}

/**
 * Ce discount arată de fapt cardurile: oferta founding, CU O EXCEPȚIE - dacă
 * omul are deja un redemption neconsumat pe alt cod (`me.firstPaymentDiscount`,
 * a venit cu un cod de referral), respectăm codul lui în loc să-l suprascriem
 * cu FOUNDING30. Întoarce null când oferta nu e activă/almost_gone (indiferent
 * de pendingDiscount - „claimed"/"none" nu schimbă prețul afișat).
 */
export function resolveFoundingDiscount(
  offer: LaunchOffer,
  pendingDiscount: Me["firstPaymentDiscount"],
): { code: string; percent: number; months: number } | null {
  if (offer.status !== "active" && offer.status !== "almost_gone") return null;
  if (pendingDiscount && pendingDiscount.code !== offer.code) return pendingDiscount;
  return { code: offer.code, percent: offer.percent, months: offer.months };
}

/**
 * Auto-redeem-ul codului ofertei (nimeni nu-l tastează manual) e permis doar
 * când: oferta chiar dă un discount, contul e eligibil, ȘI contul n-are deja
 * un redemption neconsumat (`me.firstPaymentDiscount`, pe orice cod) - un
 * pendingDiscount existent înseamnă că omul a venit cu codul lui, nu-l
 * suprascriem cu FOUNDING30.
 */
export function shouldAutoRedeemFoundingOffer(
  offer: LaunchOffer,
  plan: Pick<PlanSummary, "hasStripeSubscription" | "renewsAt">,
  pendingDiscount: Me["firstPaymentDiscount"],
): boolean {
  return (offer.status === "active" || offer.status === "almost_gone") && foundingOfferEligible(plan) && pendingDiscount === null;
}

/** Preț lunar redus, în euro (nu micro - plans.priceEur e deja euro întreg). */
export function discountedPriceEur(priceEur: number, percent: number): number {
  return Math.round(priceEur * (100 - percent)) / 100;
}

/** 13.30 (nu 13.3), dar 20 (nu 20.00) - vezi format.test.ts pentru convenția `eur()`, aici e alt caz: prețul de listă, nu micro-unități. */
export function fmtFoundingPrice(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

export type WidgetTone = "friendly" | "professional" | "playful" | "concise";
export type WidgetVoice = "standard" | "serious" | "storyteller";

export interface WidgetBranding {
  assistantName?: string;
  logoUrl?: string;
  /** iconiță predefinită în buton (id din @ai-guidance/shared/presetIcons); logoUrl are prioritate */
  presetIcon?: string;
  primaryColor?: string;
  /** cerneala peste primaryColor (text de header, ✕); gol = calculată automat */
  textColor?: string;
  /** „Just logo": bulina arată doar imaginea, fără cerc/fundal/umbră */
  logoOnly?: boolean;
  /** CSS custom (doar white-label): buton (`.aig-toggle`) și panou (`.aig-panel`) */
  launcherCss?: string;
  chatCss?: string;
  welcomeMessage?: string;
  /** răspunsul asistentului când chiar nu are un răspuns; gol = default-ul din backend */
  fallbackMessage?: string;
  tone?: WidgetTone;
  /** opt-in la asistentul „orb" (orb + card HUD întunecat, include modul voce); absent/false = panoul clasic de chat */
  orbChat?: boolean;
  /** vocea folosită în modul voce al orbului; absent = "standard" */
  voice?: WidgetVoice;
}

/** oglinda enum-ului din @ai-guidance/shared (installPlatformSchema) - tipuri locale, ca restul fișierului */
export type InstallPlatform = "wordpress" | "shopify" | "wix" | "squarespace" | "webflow" | "gtm" | "generic";

export interface AdminApp {
  appId: string;
  name: string;
  publicKey: string;
  allowedOrigins: string[];
  /** pattern-uri de URL pe care widgetul nu apare (pathname+hash, `*` wildcard, `#/...` pt. SPA) */
  excludedUrls: string[];
  /** selectoare CSS excluse din snapshotul trimis la LLM (subtree-ul întreg) - vezi sdk/snapshot.ts */
  excludedSelectors: string[];
  llmProvider: string;
  llmModel: string;
  enabled: boolean;
  live: boolean;
  widgetBranding: WidgetBranding | null;
  /** notițe libere ale proprietarului pentru agent - server-side, nu ies în /config. Null = fără. */
  agentNotes: string | null;
  /** minutele de voce per vizitator pe acest sit; null = implicitul produsului (3) */
  voiceMinutesPerVisitor: number | null;
  clientLabel: string | null;
  faviconUrl: string | null;
  /** platforma detectată de server din homepage-ul sitului; null = nedetectată (încă sau deloc) */
  detectedPlatform: InstallPlatform | null;
  /** opțiunea selectată în cardul Install - preselectată de detectare, apoi a clientului; persistată în DB */
  installPlatform: InstallPlatform | null;
  /** situl folosește Google Tag Manager - hint de instalare alternativă */
  detectedGtm: boolean;
  /** false până când scriptul instalat pe situl clientului face primul apel real către noi */
  synced: boolean;
  /** app de demo al casei - setabil DOAR de admin (bifă în settings). Decide ce app
   *  acceptă /v1/demo/ask. Vezi docs/plan-demo-gating-signup-firma.md. */
  isDemo: boolean;
  /** vocea activă pe acest app - admin-only, opt-in */
  voiceEnabled: boolean;
}

export interface AppTraffic extends AdminApp {
  /** headline-ul + data ultimului raport AI al sitului (cardul „Latest AI reports") */
  latestReportHeadline: string | null;
  latestReportAt: number | null;
  answersThisMonth: number;
  answersTotal: number;
  /** răspunsuri/zi, ultimele 30 de zile, cele mai vechi primele, zero-umplut */
  dailyCounts: number[];
  /** sesiuni distincte/zi care au pus cel puțin o întrebare (NU vizite!), aceeași fereastră/formă ca dailyCounts */
  dailySessions: number[];
  /** pâlnia sitului pe aceeași fereastră de 30 de zile */
  funnel: FunnelTotals;
}

export interface DailyPoint {
  day: string;
  count: number;
}

/**
 * Pâlnia reală a asistentului (oglinda lui `FunnelTotals` din backend).
 *
 * TREI unități diferite, NICIODATĂ adunate sau comparate direct - vezi
 * comentariul de pe `sessionStatsDaily` din backend (db/schema.ts) pentru
 * motivul complet:
 *  - `impressions` = afișări PER PAGINĂ VIZITATĂ (anonim, nededuplicabil pe
 *    vizitator - SDK-ul nu are voie să creeze un id de sesiune înainte de o
 *    interacțiune, ePrivacy art. 5(3));
 *  - `opens`/`asked` = sesiuni DISTINCTE (identificate, de la deschidere).
 *
 * `opens >= asked` e invariantul pe date produse de același SDK, dar NU e
 * garantat în fereastra de tranziție: un SDK vechi rămas în cache-ul
 * browserului trimite întrebări fără evenimente de sesiune, deci `asked` poate
 * depăși `opens`. Backendul nu corectează cifra, deci UI-ul o arată ca atare și
 * doar plafonează barele (vezi `VisitorFunnelCard` din main.tsx). NU există un
 * invariant `impressions` vs. `opens`: sunt pe unități incompatibile.
 */
export interface FunnelTotals {
  /** afișări ale widgetului, PER PAGINĂ VIZITATĂ - anonim, nededuplicabil pe vizitator */
  impressions: number;
  /** sesiuni distincte care au deschis bula */
  opens: number;
  /** sesiuni distincte care au pus cel puțin o întrebare */
  asked: number;
  /** episoade de ghidare pornite */
  guided: number;
  /** dintre ele, câte s-au terminat cu vizitatorul ajuns la țintă */
  reached: number;
}

export interface FunnelDay extends FunnelTotals {
  /** „YYYY-MM-DD" UTC */
  day: string;
}

/** Facts the assistant checked against the catalogue before stating them (guards), in the window. */
export interface ClaimCounts {
  verified: number;
  corrected: number;
}

export interface SessionFunnel {
  totals: FunnelTotals;
  /** o intrare per zi din fereastră, cea mai veche prima, zero-umplut */
  days: FunnelDay[];
}

/** Un pas din drumul vizitatorului până la întrebare (oglinda `UserActionEvent`
 *  din shared): navigare sau click/submit cu eticheta VIZIBILĂ a elementului
 *  (niciodată ce a tastat). `n` = click-uri identice consecutive, colapsate. */
export interface ConversationTrailStep {
  t: number;
  kind: "nav" | "click" | "submit";
  path: string;
  label?: string;
  n?: number;
}

export interface ConversationMessage {
  id: number;
  question: string;
  answer: string;
  path: string | null;
  title: string | null;
  /** null/absent pe rândurile vechi (SDK-ul de dinainte de trail) */
  recentActions?: ConversationTrailStep[] | null;
  createdAt: number;
  /** absent pe rândurile vechi (dinainte de canalul de voce) */
  channel?: "text" | "voice";
  /** acțiunile luate de agent în tura asta - forme neomogene, randate defensiv */
  agentActions?: unknown[] | null;
}

export interface ConversationSession {
  sessionId: string | null;
  startedAt: number;
  lastAt: number;
  messages: ConversationMessage[];
}

export interface ConversationsSummary {
  totalThisMonth: number;
  topPages: Array<{ path: string; count: number }>;
  avgSessionLength: number;
}

// --- guidance insights (tab „statistici" al unui site) ---

/** O pagină cu fricțiune (top 10), din GET /apps/:appId/insights. */
export interface InsightsFrictionPage {
  path: string;
  /** titlul paginii (best-effort, din conversații) - null = afișezi path-ul brut */
  title: string | null;
  questions: number;
  fallbacks: number;
  abandoned: number;
  targetMissing: number;
}

/** Funnel-ul de ghidare pe fereastră: întrebare → sesiune de ghidare → țintă atinsă. */
export interface InsightsFunnel {
  asked: number;
  guided: number;
  reached: number;
  abandoned: number;
}

/**
 * Corpul rezumatului AI săptămânal - oglinda lui `WeeklySummaryBody` din backend
 * (services/aiSummary.ts). Cifrele funnel-ului (`stats`) sunt calculate exact din
 * DB, nu de LLM; restul câmpurilor sunt partea calitativă scrisă de model.
 */
export interface AiSummaryBody {
  headline: string;
  frictions: Array<{ path: string; signal: string; detail: string }>;
  unmet: Array<{ theme: string; examples: string[]; approxCount: number }>;
  guidance: { comment: string };
  recommendations: string[];
  stats: { asked: number; guided: number; reached: number; abandoned: number; targetMissing: number; fallbacks: number };
}

/** Rezumatul proaspăt generat de POST /ai-summary/run (super-admin). */
export interface GeneratedWeeklySummary {
  body: AiSummaryBody;
  periodStart: number;
  periodEnd: number;
  generatedAt: number;
  model: string;
}

/**
 * Un raport AI persistat, așa cum îl întoarce GET /apps/:appId/ai-summaries.
 * `body` e același corp pe care îl arată și cardul curent - istoricul e doar
 * lista lor, în ordine descrescătoare după perioadă.
 */
export interface AiSummaryRecord {
  id: number;
  periodStart: number;
  periodEnd: number;
  generatedAt: number;
  model: string | null;
  body: AiSummaryBody;
}

/** Răspunsul GET /apps/:appId/insights?days=30 (gated pe feature `analytics`). */
export interface InsightsResponse {
  frictionPages: InsightsFrictionPage[];
  funnel: InsightsFunnel;
  trend: DailyPoint[];
  /** ultimul rezumat AI (body) sau null dacă n-a rulat încă */
  summary: AiSummaryBody | null;
  summaryGeneratedAt: number | null;
  conversationsSummary: ConversationsSummary;
}

export interface ConfigSummary {
  version: string;
  status: string;
  createdAt: number;
}

/** Consum orientat pe client - răspunsuri, nu tokeni. */
export interface AppUsage {
  monthTokens: number;
  monthCostMicroUsd: number;
  totalCostMicroUsd: number;
  accountTotalCostMicroUsd: number;
  remainingCreditMicroUsd: number;
}

export interface AdminOverview {
  totalAccounts: number;
  totalApps: number;
  mrrEur: number;
  monthResponses: number;
  monthTokens: number;
  monthCostMicroUsd: number;
  totalCostMicroUsd: number;
  monthMarginMicroUsd: number;
}

export interface Ticket {
  id: number;
  fromEmail: string;
  subject: string;
  status: string;
  lastMessageAt: number;
  createdAt: number;
  closedAt: number | null;
  /** "email" (support@) sau "feedback" (butonul Feedback din consolă) */
  source: string;
  context: { accountId: number; plan: string; screen: string; kind: "text" | "voice" } | null;
}

export interface TicketMessage {
  id: number;
  direction: string;
  fromEmail: string;
  body: string;
  createdAt: number;
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[];
}

/** Vezi backend services/codes.ts. `available` se deduce din availableAt, nu se scrie în DB. */
export type EarningStatus = "pending" | "available" | "cancelled";

export type CodeType = "promo" | "referral" | "partner";

export type BenefitType = "percent_first_payment" | "credit_eur" | "trial_days";

export interface ReferralEarning {
  id: number;
  amountMicroEur: number;
  status: EarningStatus;
  /** când banii ies din hold și devin retrăgibili */
  availableAt: number;
  createdAt: number;
}

/** Pagina de referral a clientului: codul lui + ce a adus până acum. */
export interface ReferralInfo {
  /** null cât timp nu și-a ales încă un cod */
  code: string | null;
  /** linkul gata de distribuit (duce pe situl de prezentare, nu în dashboard) */
  link: string | null;
  signups: number;
  converted: number;
  pendingMicroEur: number;
  availableMicroEur: number;
  earnings: ReferralEarning[];
  discountPercent: number;
  commissionPercent: number;
  holdDays: number;
}

export interface RedeemedBenefit {
  code: string;
  type: CodeType;
  benefitType: BenefitType;
  benefitValue: number;
}

/** Codul redeemed de un cont, așa cum îl vede consola de admin - consumat sau nu. */
export interface AppliedCode {
  code: string;
  type: CodeType;
  benefitType: BenefitType;
  benefitValue: number;
  redeemedAt: number;
  /** null = beneficiul încă n-a fost aplicat (clientul n-a plătit încă) */
  consumedAt: number | null;
  /** codul însuși mai e activ (nu dezactivat/expirat) */
  codeActive: boolean;
}

export interface AdminCodeRow {
  id: number;
  code: string;
  type: CodeType;
  ownerAccountId: number | null;
  ownerEmail: string | null;
  benefitType: BenefitType;
  benefitValue: number;
  commissionPercent: number;
  maxUses: number | null;
  currentUses: number;
  expiresAt: number | null;
  active: boolean;
  createdAt: number;
}

export interface AdminEarningRow {
  id: number;
  code: string;
  ownerAccountId: number;
  ownerEmail: string | null;
  referredAccountId: number;
  amountMicroEur: number;
  status: EarningStatus;
  availableAt: number;
  createdAt: number;
  cancelledReason: string | null;
}

export interface AdminRedemptionRow {
  accountId: number;
  email: string | null;
  redeemedAt: number;
  /** null = beneficiul încă n-a fost aplicat (clientul n-a plătit încă) */
  consumedAt: number | null;
}

export interface AdminAccountRow {
  id: number;
  email: string;
  plan: PlanId;
  planLabel: string;
  priceEur: number;
  isAdmin: boolean;
  createdAt: number;
  sites: number;
  answersUsed: number;
  answersLimit: number;
  monthTokens: number;
  monthCostMicroUsd: number;
  totalCostMicroUsd: number;
  creditMicroUsd: number;
  monthMarginMicroUsd: number;
  siteScans: number;
  manualPageScans: number;
  scanCostMicroUsd: number;
  messageCostMicroUsd: number;
  bonusAnswers: number;
  extraSites: number;
  /** grant intern de plan; null = fără. `plan`/`priceEur` rămân cele plătite. */
  grantedPlan: PlanId | null;
  isTestAccount: boolean;
  renewsAt: number | null;
  /** discount de „prima plată" neconsumat: cât are de încasat adminul, de fapt */
  firstPaymentDiscount: { code: string; percent: number; months: number } | null;
  /** Stripe trial end, ms. „În trial acum" = trialEndsAt !== null && trialEndsAt > Date.now(). */
  trialEndsAt: number | null;
  /** codul redeemed de acest cont (consumat sau nu); null = n-a folosit niciun cod */
  appliedCode: AppliedCode | null;
}

// --- Content Hub (News / Research / Blog) ---
// Oglindă manuală a formelor din backend (contentService/adminContent). Ca la
// PlanId: dashboard-ul nu importă din backend, deci ține-le sincronizate.
export type ContentKind = "news" | "research" | "blog";
export type ContentStatus = "draft" | "in_review" | "scheduled" | "published" | "archived";

export interface ContentSeo {
  titleLength: number;
  metaDescriptionLength: number;
  wordCount: number;
  hasHero: boolean;
  slugValid: boolean;
  thin: boolean;
}

export interface ContentItem {
  id: number;
  type: ContentKind;
  slug: string;
  status: ContentStatus;
  title: string;
  dek: string;
  metaDescription: string;
  excerpt: string;
  bodyMd: string;
  heroUrl: string | null;
  heroAlt: string | null;
  /**
   * Promptul folosit la generarea imaginii hero curente. Absent (nu doar null)
   * pe endpointuri care nu-l calculează (listă, create, update, tranziții de
   * status) - vezi `withHeroPrompt` vs. `withSeo` în routes/adminContent.ts.
   */
  heroImagePrompt?: string | null;
  tags: string[];
  authorName: string;
  reviewedBy: number | null;
  publishAt: number | null;
  publishedAt: number | null;
  createdAt: number;
  updatedAt: number;
  seo: ContentSeo;
}

/** Câmpurile editabile - subset al ContentItem trimis la PATCH. */
export type ContentPatch = Partial<
  Pick<ContentItem, "title" | "slug" | "dek" | "metaDescription" | "excerpt" | "bodyMd" | "heroUrl" | "heroAlt" | "tags" | "authorName">
>;

export type DistributionStatus = "pending" | "sent" | "failed" | "cancelled";
export type ScheduleMode = "publish" | "offset" | "at";

export interface ContentDistribution {
  id: number;
  itemId: number;
  channel: string;
  status: DistributionStatus;
  scheduleMode: ScheduleMode;
  scheduledAt: number | null;
  offsetMinutes: number | null;
  text: string;
  attempts: number;
  lastError: string | null;
  externalId: string | null;
  sentAt: number | null;
  createdAt: number;
  /** scadența derivată (backend o calculează din publicarea articolului). */
  dueAt: number | null;
}

export interface SocialChannelInfo {
  channel: string;
  configured: boolean;
}

export interface ContentRevision {
  id: number;
  itemId: number;
  revisionNo: number;
  source: "ai" | "admin";
  instruction: string | null;
  snapshot: Record<string, unknown>;
  createdBy: number | null;
  createdAt: number;
}

export const api = {
  /** Datele de firmă sunt OBLIGATORII (serviciul e B2B). Codul de referral nu se
   *  mai trimite din formular: backend-ul îl ia singur din cookie-ul `amwai_ref`
   *  lăsat de landing (?ref=COD); un cod pe hârtie se introduce la checkout. */
  register: (email: string, password: string, business: BusinessProfileInput, marketingConsent?: boolean) =>
    request("/register", {
      method: "POST",
      body: JSON.stringify({ email, password, ...business, marketingConsent }),
    }) as Promise<{
      // `account` nu e folosit de UI momentan - după register, fluxul trimite
      // userul spre login (vezi AuthView.submit), nu direct în dashboard.
      account: unknown;
      sessionToken: string;
      /** false = emailul de verificare NU a plecat (trimiterea a eșuat) - UI-ul trebuie să spună asta */
      emailSent: boolean;
      refCode?: { applied: true; benefit: RedeemedBenefit } | { applied: false; error: string };
    }>,

  /** Completează/actualizează datele de firmă (poarta de profil + editare). */
  updateProfile: (business: BusinessProfileInput) =>
    request("/account/profile", { method: "POST", body: JSON.stringify(business) }) as Promise<{
      ok: true;
      profileComplete: boolean;
      business: BusinessProfile;
    }>,

  login: (email: string, password: string) =>
    request("/login", { method: "POST", body: JSON.stringify({ email, password }) }) as Promise<{
      sessionToken: string;
    }>,

  /** Best-effort: apelantul nu trebuie să blocheze deslogarea locală dacă
   *  serverul nu răspunde - vezi App.logout/manualLogout în main.tsx. */
  logout: () => request("/logout", { method: "POST" }) as Promise<void>,

  /** Revocă TOATE sesiunile contului, nu doar pe cea curentă. */
  logoutAll: () => request("/logout-all", { method: "POST" }) as Promise<void>,

  /** Cheia API programatică (feature `api`) - afișată o singură dată de backend. */
  rotateApiKey: () =>
    request("/api-key/rotate", { method: "POST" }) as Promise<{ secretKey: string }>,

  /** Feedback către noi (doar planuri plătite). Doar text: dictarea e a browserului. */
  sendFeedback: (body: { text: string; dictated: boolean; screen: string }) =>
    request("/feedback", { method: "POST", body: JSON.stringify(body) }) as Promise<{ ok: true }>,

  setMarketingConsent: (consent: boolean) =>
    request("/account/marketing-consent", { method: "POST", body: JSON.stringify({ consent }) }) as Promise<{
      ok: true;
    }>,

  /** Noua zi ia efect abia la finalul ciclului curent (deferrare anti-abuz) -
   *  vezi `effectiveAt` în răspuns. */
  setStatsResetDay: (day: number) =>
    request("/account/stats-reset-day", { method: "POST", body: JSON.stringify({ day }) }) as Promise<{
      ok: boolean;
      statsResetDay: number;
      effectiveAt: number;
    }>,

  googleClientId: () => request("/google-client-id") as Promise<{ clientId: string }>,

  googleAuth: (credential: string) =>
    request("/google", { method: "POST", body: JSON.stringify({ credential }) }) as Promise<{
      account: unknown;
      sessionToken: string;
      refCode?: { applied: true; benefit: RedeemedBenefit } | { applied: false; error: string };
    }>,

  resendVerification: (email: string) =>
    request("/resend-verification", { method: "POST", body: JSON.stringify({ email }) }) as Promise<{ ok: true; emailSent: boolean }>,

  forgotPassword: (email: string) =>
    request("/forgot-password", { method: "POST", body: JSON.stringify({ email }) }) as Promise<{ ok: true }>,

  resetPassword: (token: string, password: string) =>
    request("/reset-password", { method: "POST", body: JSON.stringify({ token, password }) }) as Promise<{
      ok: true;
    }>,

  /** Schimbarea parolei din dashboard (cont autentificat). Revocă celelalte sesiuni,
   *  o păstrează pe cea curentă. 401 invalid_credentials / 400 same_password. */
  changePassword: (currentPassword: string, newPassword: string) =>
    request("/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }) as Promise<{
      ok: true;
    }>,

  me: () => request("/me") as Promise<Me>,

  /**
   * Endpoint PUBLIC, fără auth - nu trece prin `request()` (care prefixează
   * /v1/admin și atașează x-admin-key). Cacheable 60s server-side, se cheamă
   * o singură dată la mount în PlanView. Orice eroare de rețea/parsare =
   * tratată ca "none": un banner promoțional nu are voie să blocheze pagina
   * de preț dacă fetch-ul eșuează.
   */
  launchOffer: async (): Promise<LaunchOffer> => {
    try {
      const res = await fetch("/v1/public/launch-offer");
      if (!res.ok) return { status: "none" };
      const data = (await res.json().catch(() => null)) as LaunchOffer | null;
      if (!data || typeof data !== "object" || !("status" in data)) return { status: "none" };
      return data;
    } catch {
      return { status: "none" };
    }
  },

  /**
   * URL de redirect către Stripe Checkout (prima subscripție). `billing` =
   * țara (+ codul poștal la ES) declarate înainte - decid IGIC-ul canar;
   * adresa reală rămâne cea completată în Checkout.
   */
  checkout: (plan: PlanId, billing: BillingLocation) =>
    request("/billing/checkout", { method: "POST", body: JSON.stringify({ plan, billing }) }) as Promise<{
      url: string;
    }>,

  /** URL de redirect către Stripe Billing Portal (card, facturi, cancel). */
  billingPortal: () => request("/billing/portal", { method: "POST" }) as Promise<{ url: string }>,

  /**
   * Schimbare de plan pe abonamentul existent. `effective`: "now" (upgrade,
   * diferență pro-rata facturată imediat) | "period_end" (downgrade) |
   * "cancelled_downgrade" (planul curent re-ales anulează downgrade-ul).
   */
  changePlan: (plan: PlanId) =>
    request("/billing/change-plan", { method: "POST", body: JSON.stringify({ plan }) }) as Promise<{
      ok: true;
      effective: "now" | "period_end" | "cancelled_downgrade";
    }>,

  /** Reia un abonament anulat la finalul perioadei. Starea din /me se schimbă la webhook, nu imediat. */
  resumeSubscription: () => request("/billing/resume", { method: "POST" }) as Promise<{ ok: true }>,

  /** Ștergere (soft, 90 de zile) a contului propriu; cere parola curentă. 401 = parolă greșită. */
  deleteAccount: (password: string) =>
    request("/account/delete", { method: "POST", body: JSON.stringify({ password }) }) as Promise<{ ok: true }>,

  /** URL de Checkout one-time pentru blocuri de extra answers (€15/500).
   * `billing` doar când contul n-are încă adresă în Stripe (aia e autoritară). */
  topup: (blocks: number, billing?: BillingLocation) =>
    request("/billing/topup", { method: "POST", body: JSON.stringify({ blocks, ...(billing ? { billing } : {}) }) }) as Promise<{
      url: string;
    }>,

  /** Setează sloturile „extra site" pe abonament; oglinda vine prin webhook. */
  setExtraSites: (quantity: number) =>
    request("/billing/extra-sites", { method: "POST", body: JSON.stringify({ quantity }) }) as Promise<{
      ok: true;
      extraSites: number;
    }>,

  /** Codul personal + statistici. `code` e null până când clientul își alege unul. */
  referral: () => request("/account/referral") as Promise<ReferralInfo>,

  /** Alege sau schimbă codul personal. Erori: code_taken | code_reserved | invalid_format | same_code. */
  claimReferralCode: (code: string) =>
    request("/account/referral/code", { method: "POST", body: JSON.stringify({ code }) }) as Promise<{
      ok: true;
      code: string;
      previous: string | null;
    }>,

  /** Pentru cine n-a pus codul la signup - valabil doar înainte de prima plată. */
  redeemCode: (code: string) =>
    request("/account/redeem-code", { method: "POST", body: JSON.stringify({ code }) }) as Promise<{
      ok: true;
      benefit: RedeemedBenefit;
    }>,

  // Self-service credit a fost eliminat (era bani gratis, fără plată - audit S-3).
  // Creditul de overage se acordă acum admin-only, prin consola de admin.

  listApps: () => request("/apps") as Promise<{ apps: AdminApp[] }>,

  appsTraffic: () => request("/apps-traffic") as Promise<{ apps: AppTraffic[] }>,

  statsDaily: (days = 30, appId?: string) =>
    request(`/stats/daily?days=${days}${appId ? `&appId=${encodeURIComponent(appId)}` : ""}`) as Promise<{
      days: DailyPoint[];
      funnel: SessionFunnel;
      claims: ClaimCounts;
    }>,

  conversations: (appId: string, opts: { page?: number; pageSize?: number; from?: number; to?: number; summary?: boolean; channel?: "text" | "voice" } = {}) => {
    const q = new URLSearchParams();
    q.set("page", String(opts.page ?? 1));
    q.set("pageSize", String(opts.pageSize ?? 8));
    if (opts.from !== undefined) q.set("from", String(opts.from));
    if (opts.to !== undefined) q.set("to", String(opts.to));
    if (opts.summary === false) q.set("summary", "false");
    if (opts.channel) q.set("channel", opts.channel);
    return request(`/apps/${appId}/conversations?${q}`) as Promise<{
      sessions: ConversationSession[];
      summary: ConversationsSummary | null;
      page: number;
      pageSize: number;
      totalSessions: number;
      totalPages: number;
    }>;
  },

  /** Insights de ghidare pentru un site (funnel + fricțiune + rezumat AI). Gated
   *  pe feature `analytics` - 403 feature_not_available altfel, ca /conversations. */
  insights: (appId: string, days = 30) =>
    request(`/apps/${appId}/insights?days=${days}`) as Promise<InsightsResponse>,

  /** Istoricul rapoartelor AI ale unui site (cel mai recent întâi). Gated pe
   *  feature `analytics`, ca /insights. Se cere leneș, la deschiderea istoricului. */
  aiSummaries: (appId: string, limit = 12) =>
    request(`/apps/${appId}/ai-summaries?limit=${limit}`) as Promise<{ summaries: AiSummaryRecord[] }>,

  /** Declanșare manuală a rezumatului AI săptămânal - DOAR super-admin. Întoarce
   *  rezumatul generat sau `{ skipped: true }` dacă n-au fost date în fereastră.
   *  UI-ul re-fetch-uiește insights la succes, deci forma exactă a summary-ului
   *  contează mai puțin decât distincția skipped/nu. */
  runAiSummary: (appId: string) =>
    request(`/apps/${appId}/ai-summary/run`, { method: "POST" }) as Promise<
      { summary: GeneratedWeeklySummary } | { skipped: true }
    >,

  createApp: (name: string, allowedOrigins: string[]) =>
    request("/apps", { method: "POST", body: JSON.stringify({ name, allowedOrigins }) }) as Promise<{
      app: AdminApp;
    }>,

  updateApp: (appId: string, patch: Partial<Pick<AdminApp, "name" | "allowedOrigins" | "excludedUrls" | "excludedSelectors" | "enabled" | "live" | "clientLabel">>) =>
    request(`/apps/${appId}`, { method: "PATCH", body: JSON.stringify(patch) }) as Promise<{ app: AdminApp }>,

  updateBranding: (appId: string, branding: WidgetBranding) =>
    request(`/apps/${appId}/branding`, { method: "PATCH", body: JSON.stringify(branding) }) as Promise<{
      app: AdminApp;
    }>,

  updateInstallPlatform: (appId: string, platform: InstallPlatform) =>
    request(`/apps/${appId}/install-platform`, { method: "PATCH", body: JSON.stringify({ platform }) }) as Promise<{
      app: AdminApp;
    }>,

  updateAgentNotes: (appId: string, notes: string) =>
    request(`/apps/${appId}/agent-notes`, { method: "PATCH", body: JSON.stringify({ notes }) }) as Promise<{
      app: AdminApp;
    }>,

  updateVoiceMinutes: (appId: string, minutes: number | null) =>
    request(`/apps/${appId}/voice-minutes`, { method: "PATCH", body: JSON.stringify({ minutes }) }) as Promise<{
      app: AdminApp;
    }>,

  uploadBrandingLogo: (appId: string, file: Blob) => {
    const form = new FormData();
    form.append("logo", file, "logo.png");
    return request(`/apps/${appId}/branding/logo`, { method: "POST", body: form }) as Promise<{ url: string }>;
  },

  deleteApp: (appId: string) => request(`/apps/${appId}`, { method: "DELETE" }) as Promise<{ ok: true }>,

  listConfigs: (appId: string) =>
    request(`/apps/${appId}/configs`) as Promise<{ configs: ConfigSummary[] }>,

  getConfig: (appId: string, version: string) =>
    request(`/apps/${appId}/configs/${version}`) as Promise<{ version: string; status: string; body: unknown }>,

  usage: (appId: string) => request(`/apps/${appId}/usage`) as Promise<AppUsage>,

  builderToken: (appId: string) =>
    request(`/apps/${appId}/builder-token`, { method: "POST" }) as Promise<{
      token: string;
      url: string;
      expiresInSeconds: number;
    }>,

  publishBuilderDraft: (appId: string) =>
    request(`/apps/${appId}/publish-draft`, { method: "POST" }) as Promise<{ ok: true; published: string }>,

  // --- consola de super-admin ---
  adminOverview: () => request("/console/overview") as Promise<AdminOverview>,

  adminAccounts: () =>
    request("/console/accounts") as Promise<{ accounts: AdminAccountRow[]; plans: Plan[] }>,

  /**
   * Feed global de audit (intern). `email` filtrează pe un cont - la un ticket
   * știm adresa, nu id-ul. `before` = paginare keyset pe id.
   */
  adminAudit: (opts: { action?: string; email?: string; from?: number; to?: number; before?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    q.set("limit", String(opts.limit ?? 50));
    if (opts.action) q.set("action", opts.action);
    if (opts.email) q.set("email", opts.email);
    if (opts.from !== undefined) q.set("from", String(opts.from));
    if (opts.to !== undefined) q.set("to", String(opts.to));
    if (opts.before !== undefined) q.set("before", String(opts.before));
    return request(`/console/audit?${q}`) as Promise<{ entries: AuditEntry[] }>;
  },

  adminSetPlan: (accountId: number, plan: PlanId) =>
    request(`/console/accounts/${accountId}/plan`, { method: "POST", body: JSON.stringify({ plan }) }) as Promise<{
      ok: true;
      plan: PlanId;
    }>,

  /** Admin-only: marchează/demarcă un app ca demo al casei (bifă din settings). */
  adminSetDemo: (appId: string, isDemo: boolean) =>
    request(`/console/apps/${appId}/demo`, { method: "POST", body: JSON.stringify({ isDemo }) }) as Promise<{
      ok: true;
      app: AdminApp;
    }>,

  /** Admin-only: aprinde/stinge modulul de conversație vocală pe un app (bifă din settings). */
  adminSetVoice: (appId: string, voiceEnabled: boolean) =>
    request(`/console/apps/${appId}/voice`, { method: "POST", body: JSON.stringify({ voiceEnabled }) }) as Promise<{
      ok: true;
      app: AdminApp;
    }>,

  /**
   * Admin-only: acordă (sau revocă, cu `null`) grantul intern de plan. NU e o
   * schimbare de plan - `adminSetPlan` face aia. Aici clientul primește
   * capabilitățile și volumele unui plan superior peste ce plătește, fără nicio
   * atingere de Stripe. `overLimit` spune ce rămâne peste limite după schimbare
   * (relevant la revocare).
   */
  adminSetGrantedPlan: (accountId: number, grantedPlan: PlanId | null) =>
    request(`/console/accounts/${accountId}/granted-plan`, {
      method: "POST",
      body: JSON.stringify({ grantedPlan }),
    }) as Promise<{
      ok: true;
      grantedPlan: PlanId | null;
      effectivePlan: PlanId;
      overLimit: { sites: number; answers: number };
    }>,

  adminGrantAddons: (accountId: number, deltas: { bonusAnswers?: number; extraSites?: number }) =>
    request(`/console/accounts/${accountId}/addons`, { method: "POST", body: JSON.stringify(deltas) }) as Promise<{
      bonusAnswers: number;
      extraSites: number;
    }>,

  /** `chargedMicroEur` = suma efectiv încasată (prețul planului, minus un eventual discount). */
  adminMarkPaid: (accountId: number) =>
    request(`/console/accounts/${accountId}/mark-paid`, { method: "POST" }) as Promise<{
      ok: true;
      renewsAt: number;
      chargedMicroEur: number;
      discountPercent: number;
    }>,

  /** `trialEnd` în ms - noua dată de sfârșit de trial pe abonamentul Stripe. */
  adminExtendTrial: (accountId: number, trialEnd: number) =>
    request(`/console/accounts/${accountId}/extend-trial`, { method: "POST", body: JSON.stringify({ trialEnd }) }) as Promise<{
      ok: true;
      trialEnd: number;
    }>,

  /** Deschide o sesiune de dashboard pe contul clientului, marcată ca impersonare. */
  adminImpersonate: (accountId: number) =>
    request(`/console/accounts/${accountId}/impersonate`, { method: "POST" }) as Promise<{ sessionToken: string }>,

  adminCodes: () => request("/console/codes") as Promise<{ codes: AdminCodeRow[] }>,

  adminCreateCode: (input: {
    code: string;
    type: "promo" | "partner";
    benefitType: BenefitType;
    benefitValue: number;
    commissionPercent?: number;
    maxUses?: number | null;
    expiresAt?: number | null;
    ownerAccountId?: number | null;
  }) =>
    request("/console/codes", { method: "POST", body: JSON.stringify(input) }) as Promise<{
      ok: true;
      id: number;
      code: string;
    }>,

  adminDeactivateCode: (id: number) =>
    request(`/console/codes/${id}/deactivate`, { method: "POST" }) as Promise<{ ok: true }>,

  adminCodeRedemptions: (id: number) =>
    request(`/console/codes/${id}/redemptions`) as Promise<{ redemptions: AdminRedemptionRow[] }>,

  adminEarnings: () => request("/console/earnings") as Promise<{ earnings: AdminEarningRow[] }>,

  /** `force` e cerut explicit pentru comisioanele ieșite deja din hold. */
  adminCancelEarning: (id: number, reason: string, force?: boolean) =>
    request(`/console/earnings/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason, force }) }) as Promise<{
      ok: true;
    }>,

  /**
   * `to` absent = fără limită superioară, ca să nu scape tichetele din minutul
   * curent (vezi dateInputToMs în main.tsx) - filtrele merg până în backend,
   * nu doar client-side, ca lista să nu crească necontrolat cu timpul.
   */
  adminTickets: (opts: { statuses?: string[]; from?: number; to?: number } = {}) => {
    const q = new URLSearchParams();
    for (const s of opts.statuses ?? []) q.append("status", s);
    if (opts.from !== undefined) q.set("from", String(opts.from));
    if (opts.to !== undefined) q.set("to", String(opts.to));
    const qs = q.toString();
    return request(`/console/tickets${qs ? `?${qs}` : ""}`) as Promise<{ tickets: Ticket[] }>;
  },

  adminTicket: (id: number) => request(`/console/tickets/${id}`) as Promise<{ ticket: TicketDetail }>,

  adminReplyTicket: (id: number, message: string) =>
    request(`/console/tickets/${id}/reply`, { method: "POST", body: JSON.stringify({ message }) }) as Promise<{
      ok: true;
    }>,

  adminSetTicketStatus: (id: number, status: "open" | "pending" | "closed") =>
    request(`/console/tickets/${id}/status`, { method: "POST", body: JSON.stringify({ status }) }) as Promise<{
      ok: true;
    }>,

  // --- Content Hub (News / Research / Blog), etapa E2 ---
  contentList: (type: ContentKind) =>
    request(`/content/${type}`) as Promise<{ type: ContentKind; label: string; items: ContentItem[] }>,

  contentGet: (id: number) =>
    request(`/content/item/${id}`) as Promise<{ item: ContentItem; previewToken: string | null }>,

  contentCreate: (type: ContentKind) =>
    request("/content", { method: "POST", body: JSON.stringify({ type }) }) as Promise<{ item: ContentItem }>,

  contentUpdate: (id: number, patch: ContentPatch) =>
    request(`/content/item/${id}`, { method: "PATCH", body: JSON.stringify(patch) }) as Promise<{ item: ContentItem }>,

  contentSuggestSlug: (type: ContentKind, title: string) =>
    request(`/content/${type}/suggest-slug?title=${encodeURIComponent(title)}`) as Promise<{ slug: string }>,

  contentSubmit: (id: number) =>
    request(`/content/item/${id}/submit`, { method: "POST" }) as Promise<{ item: ContentItem }>,

  contentReject: (id: number) =>
    request(`/content/item/${id}/reject`, { method: "POST" }) as Promise<{ item: ContentItem }>,

  /** Generează primul draft cu AI (Gemini). Asincron, 10–40s. */
  contentGenerate: (id: number, brief?: string) =>
    request(`/content/item/${id}/generate`, { method: "POST", body: JSON.stringify(brief ? { brief } : {}) }) as Promise<{ item: ContentItem }>,

  /** Revizuiește draftul curent după o instrucțiune în limbaj natural. */
  contentRevise: (id: number, instruction: string) =>
    request(`/content/item/${id}/revise`, { method: "POST", body: JSON.stringify({ instruction }) }) as Promise<{ item: ContentItem }>,

  /** Generează (sau regenerează) imaginea hero. Fără prompt: derivat din titlu+dek pe server. */
  contentGenerateImage: (id: number, prompt?: string) =>
    request(`/content/item/${id}/generate-image`, { method: "POST", body: JSON.stringify(prompt ? { prompt } : {}) }) as Promise<{ item: ContentItem }>,

  /** publishAt absent → publică acum; prezent și în viitor → programează. */
  contentApprove: (id: number, publishAt?: number) =>
    request(`/content/item/${id}/approve`, { method: "POST", body: JSON.stringify(publishAt ? { publishAt } : {}) }) as Promise<{ item: ContentItem }>,

  contentPublish: (id: number) =>
    request(`/content/item/${id}/publish`, { method: "POST" }) as Promise<{ item: ContentItem }>,

  /** Re-programează un articol deja aprobat (scheduled) la o altă dată. */
  contentSchedule: (id: number, publishAt: number) =>
    request(`/content/item/${id}/schedule`, { method: "POST", body: JSON.stringify({ publishAt }) }) as Promise<{ item: ContentItem }>,

  contentArchive: (id: number) =>
    request(`/content/item/${id}/archive`, { method: "POST" }) as Promise<{ item: ContentItem }>,

  /** Ștergere fizică - DOAR pentru drafturi (backendul refuză orice altceva cu 409). */
  contentDelete: (id: number) =>
    request(`/content/item/${id}`, { method: "DELETE" }) as Promise<{ ok: true }>,

  contentRevisions: (id: number) =>
    request(`/content/item/${id}/revisions`) as Promise<{ revisions: ContentRevision[] }>,

  contentAddRevision: (id: number, note?: string) =>
    request(`/content/item/${id}/revisions`, { method: "POST", body: JSON.stringify({ note }) }) as Promise<{ revision: ContentRevision }>,

  // --- distribuție socială (E5) ---
  contentChannels: () =>
    request("/content/social/channels") as Promise<{ channels: SocialChannelInfo[] }>,

  contentDistributions: (id: number) =>
    request(`/content/item/${id}/distributions`) as Promise<{ distributions: ContentDistribution[] }>,

  contentAddDistribution: (id: number, input: { channel: string; scheduleMode: ScheduleMode; scheduledAt?: number; offsetMinutes?: number; text: string }) =>
    request(`/content/item/${id}/distributions`, { method: "POST", body: JSON.stringify(input) }) as Promise<{ distribution: ContentDistribution }>,

  contentCancelDistribution: (distId: number) =>
    request(`/content/distributions/${distId}/cancel`, { method: "POST" }) as Promise<{ ok: true }>,
};
